import { columns as tableColumns } from './columns';

import styles from './BboxVesselsModal.module.scss';
import { LegacyRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SortingInterface } from 'app/pages/VesselsPage/VesselsPage';
import { Table, Pagination, Button, Typo } from 'app/components';

import { useGetVesselCatalogMutation } from 'app/api/facade';
import { Bbox, TargetCatalogSearchRequest, OrderParams, TargetCatalogEntryInfo } from 'ais-facade-api/api';
import { Icon } from '../Icon/Icon';
import { throttle } from 'app/helpers';
import { useTranslation } from 'react-i18next';
import { Filters } from 'app/types/types';

interface Props {
  onClose: () => void;
  bbox: Bbox | undefined;
  innerRef?: LegacyRef<HTMLDivElement>;
  filters?: Filters;
}

const initialSort: OrderParams = {
  direction: 'DESC',
  by: 'LUT',
};

export const BboxVesselsModal = ({ innerRef, bbox, onClose, filters }: Props) => {
  const { t } = useTranslation();
  const container = useRef<HTMLDivElement>(null);

  const initialBody: TargetCatalogSearchRequest = useMemo(
    () => ({
      filter: {
        bbox,
      },
      order: {
        direction: 'DESC',
        by: 'LUT',
      },
      pagination: {
        offset: 0,
        limit: 20,
      },
    }),
    [bbox]
  );

  const [searchByFilter, { ...vesselsInBboxRequest }] = useGetVesselCatalogMutation({ fixedCacheKey: 'bbox-vessels-modal' });

  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState<TargetCatalogEntryInfo[]>();
  const [total, setTotal] = useState<number>(0);
  const currentOffset = useMemo(() => (currentPage - 1) * 20, [currentPage]);
  const [sorting, setSorting] = useState<SortingInterface>(initialSort);

  const debouncedSearchByFilter = useCallback(throttle(searchByFilter, 200), []);
  useEffect(() => {
    if (vesselsInBboxRequest.data && vesselsInBboxRequest.data.results && !vesselsInBboxRequest.isLoading) {
      setCurrentData(vesselsInBboxRequest.data.results);
    }

  }, [vesselsInBboxRequest.data, vesselsInBboxRequest.isLoading]);

  useEffect(() => {
    debouncedSearchByFilter({
      ...initialBody,
      pagination: {
        offset: currentOffset,
        limit: 20,
      },
      order:
        sorting.direction === 'default'
          ? initialSort
          : {
            direction: sorting.direction,
            by: sorting.by,
          },
      filter: {
        ...initialBody.filter,
        fleetOnly: filters?.fleetOnly || false,
        source: filters?.sourceAll ? [] : filters?.source,
        type: filters?.typeAll ? [] : filters?.type,
        country: filters?.countryAll ? [] : filters?.country,
        status: filters?.statusAll ? [] : filters?.status,
      },
    });
  }, [currentOffset]);

  useEffect(() => {
    setCurrentPage(1);

    debouncedSearchByFilter({
      ...initialBody,
      order:
        sorting.direction === 'default'
          ? initialSort
          : {
            direction: sorting.direction,
            by: sorting.by,
          },
      filter: {
        ...initialBody.filter,
        fleetOnly: filters?.fleetOnly || false,
        source: filters?.sourceAll ? [] : filters?.source,
        type: filters?.typeAll ? [] : filters?.type,
        country: filters?.countryAll ? [] : filters?.country,
        status: filters?.statusAll ? [] : filters?.status,
      },
    });
  }, [sorting]);

  useEffect(() => {
    if (vesselsInBboxRequest.data?.total) {
      setTotal(vesselsInBboxRequest.data?.total)
    }
  }, [vesselsInBboxRequest.data?.total]);

  return (
    <div className={styles.root}>
      <div className={styles.wrapper} ref={innerRef}>
        <div className={styles.panel_header}>
          <Typo size="m" value={t('ShowBboxVesselsTitle')} />{' '}
          <div onClick={onClose} className={styles.close_button}>
            <Icon name="close-16" size="16" />
          </div>
        </div>
        <Typo
          className={styles.total}
          variant="div"
          size="m"
          value={`${t('BboxVesselsTotal')}${filters?.touched ? ` ${t('AppliedFilters')}:` : ':'} ${vesselsInBboxRequest.data ? vesselsInBboxRequest.data.total : '–'}`}
        />

        <div className={styles.dialog_modal}>
          <div className={styles.table_container} ref={container}>
            {!vesselsInBboxRequest.isLoading && currentData?.length === 0 &&
              <Typo variant="p"
                size="m" className={styles.no_data} value={t('NoResultsFound')} />
            }
            {vesselsInBboxRequest.isUninitialized &&
              <Typo variant="p"
                size="m" className={styles.no_data} value={t('DataIsLoading')} />
            }
            {currentData && <Table
              manualSort={true}
              sorting={sorting}
              setSorting={setSorting}
              columns={tableColumns}
              data={currentData}
            />}
          </div>

          <div className={styles.pagination_container}>
            <Pagination
              totalCount={total}
              currentPage={currentPage}
              pageChangeHandler={setCurrentPage}
              rowsPerPage={20}
            />
            <div className={styles.actions}>
              <Button
                className={styles.action_button}
                type="button"
                label={t('ButtonLabelClose')}
                variant="general"
                onClick={onClose}
              />
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};
